import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/shared/auth';
import { toError } from '@/shared/lib/toError';
import { useCancelPortalSession } from '../api/useCancelPortalSession';
import { formatSubscriptionDate } from '../../model/formatSubscriptionDate';

type Props = {
  onMutationError?: (error: Error) => void;
};

export const CancelSubscriptionRow = ({ onMutationError }: Props = {}) => {
  const { t, i18n } = useTranslation();
  const { sessionUser } = useAuth();
  const { mutate, isPending } = useCancelPortalSession();

  const cancelAtPeriodEnd = sessionUser?.subscription.cancelAtPeriodEnd ?? false;
  const expiresAt = sessionUser?.subscription.expiresAt ?? null;

  const getScheduledLabel = (): string | null => {
    if (!cancelAtPeriodEnd) {
      return null;
    }
    if (!expiresAt) {
      return t('subscription.cancel.scheduledNoDate');
    }
    return t('subscription.cancel.scheduled', { date: formatSubscriptionDate(expiresAt, i18n.language) });
  };
  const scheduledLabel = getScheduledLabel();

  const handleSuccess = (data: { portalUrl: string }) => {
    window.location.href = data.portalUrl;
  };

  const handleError = (error: unknown) => {
    onMutationError?.(toError(error));
  };

  const handleClick = () => {
    mutate(
      { data: { flow: 'subscription_cancel' } },
      {
        onSuccess: handleSuccess,
        onError: handleError,
      }
    );
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Stack spacing={0.5}>
        <Typography variant="body1">{t('subscription.cancel.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('subscription.cancel.subtitle')}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
        {scheduledLabel && (
          <Typography variant="body2" color="warning.main" fontWeight={500}>
            {scheduledLabel}
          </Typography>
        )}
        <Button
          variant="outlined"
          onClick={handleClick}
          disabled={isPending || cancelAtPeriodEnd}
          sx={{ borderColor: 'error.dark', color: 'error.light' }}
        >
          {t('subscription.cancel.button')}
        </Button>
      </Stack>
    </Box>
  );
};
